/**
 * FieldSortModal (Unit 5 Phase 1) — multi-level sort editor.
 *
 * NO ModalShell, for the reason FieldColumnsModal.jsx:4-9 already records:
 * that shared component exposes only title / children / footer, has no header
 * slot, and is shared with the warehouse and inventory modals, so widening it
 * would put those in this unit's blast radius. Own chrome, three bands —
 * fixed header, flex:1 scrolling body, fixed footer — the same shape .frl and
 * .fcm use, so the body is the only thing that ever scrolls.
 *
 * WORKING COPY, unlike the Columns picker. FieldColumnsModal deliberately has
 * NO apply step (:11-16): every toggle lands live because a rep hiding ten
 * columns wants each tap to take effect. Sort is the opposite shape — a
 * half-built two-level sort is not a state anyone wants the grid to be in
 * while they are still building it, and reordering 800 rows under a finger
 * mid-edit is worse than useless. So:
 *
 *   copy on open  →  edit the draft  →  Apply commits  →  Cancel/✕ discards
 *
 * Mount IS open (the host mount-gates on tmgFieldSortOpen), so the initial
 * copy is a lazy useState initialiser and there is no open-transition effect
 * to keep in sync.
 *
 * CLEAR WIPES THE DRAFT, NOT THE STORED SORT, and does not close. It is an
 * edit like any other — the rep still taps Apply to commit an empty sort, or
 * Cancel to keep what they had. Making Clear a live commit would put one
 * destructive control outside the transaction the other three are inside,
 * which is exactly the inconsistency the working-copy pattern exists to avoid.
 *
 * FIELD PICKER ORDER — Bob's buildFieldOptions:9200-9217, ported: visible
 * fields first, then a DISABLED "── Hidden fields ──" separator option, then
 * the hidden ones. A hidden column is still a sortable field (hiding a column
 * hides its cells, never its data — the same MATCHING-vs-DISPLAY split the
 * SEARCHABLE_FIELDS docblock in editableFields.js states for search), so the
 * hidden fields have to be reachable; putting them behind a separator keeps
 * the list a rep actually uses at the top.
 *
 * ⚠ THAT PICKER NOW LIVES IN FieldPickerOptions (Unit 5 Phase 2). It was
 * inline here through Phase 1; Filter needs the identical list, and the Phase 1
 * device pass found TOOL ID sitting last in it. Both are reasons for one
 * definition rather than two, so the split, the separator, the escape hatch and
 * the TOOL ID pin all moved there and this file consumes them. Nothing about
 * the rendered list changed here EXCEPT the pin.
 *
 * NO "KEEP SORTED" TOGGLE. Bob's prototype declares one, assigns it, persists
 * it and reads it back — at ZERO sites that do anything with it. It is dead
 * chrome in 565KB of prototype, and it is omitted here deliberately rather
 * than ported and left equally dead.
 */

import React, { useEffect, useState } from 'react';
import FieldPickerOptions, { firstPickerKey } from './FieldPickerOptions';
import './FieldSortModal.css';

/** A new level defaults to the first offerable field, ascending. */
const DEFAULT_DIRECTION = 'asc';

/* Stable empty defaults, module scope and never mutated — the same reasoning
   as FieldRecordsList's EMPTY_HIDDEN / EMPTY_DIRTY_IDS: a missing prop must
   render an empty picker, not throw on .has() or .filter(). */
const EMPTY_FIELDS = [];
const EMPTY_HIDDEN = new Set();

export default function FieldSortModal({
  fields = EMPTY_FIELDS, hidden = EMPTY_HIDDEN, sort, onApply, onClose,
}) {
  // THE WORKING COPY. Levels are cloned, not aliased — editing a direction
  // must not mutate the committed array the grid is still rendering from.
  const [draft, setDraft] = useState(
    () => (Array.isArray(sort) ? sort.map((l) => ({ ...l })) : []),
  );

  // What "+ Add sort level" seeds with. The shared helper owns the band order,
  // so this is TOOL ID whenever the current view offers it — the Phase 1
  // behaviour was ALT TOOL ID, because the pin did not exist yet.
  const firstKey = firstPickerKey(fields, hidden);

  // Escape (desktop) — capture + stopPropagation, this modal only.
  //
  // NEW IN PHASE 2, and it is a fix rather than an addition. Phase 1 had no
  // handler here and relied on FieldViewPanel's: its navigate(-1) pops the TOP
  // history entry, which happens to be this modal's sentinel, so Escape
  // closed the right thing by coincidence. Phase 2 gates that panel listener
  // (escapeEnabled) to stop it double-firing under the Filter modal's value
  // picker, so this layer now has to own its own key — same outcome as before,
  // reached deliberately.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const setLevel = (index, patch) => {
    setDraft((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const removeLevel = (index) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addLevel = () => {
    if (!firstKey) return;
    setDraft((prev) => [...prev, { fieldKey: firstKey, direction: DEFAULT_DIRECTION }]);
  };

  const toggleDirection = (index) => {
    setDraft((prev) => prev.map((l, i) => (
      i === index ? { ...l, direction: l.direction === 'asc' ? 'desc' : 'asc' } : l
    )));
  };

  // Draft-only. See CLEAR WIPES THE DRAFT above — this does not close and does
  // not commit.
  const handleClear = () => setDraft([]);

  // The host owns the close: its handler commits and then pops the sentinel,
  // so Apply resolves through the SAME single navigate(-1) every other close
  // path does. This component never navigates.
  const handleApply = () => onApply(draft);

  return (
    <div
      className="fsm"
      data-testid="field-sort-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Sort"
    >
      <div className="fsm-header">
        <h2 className="fsm-title">Sort</h2>
        <button
          type="button"
          className="fsm-bulk"
          data-testid="field-sort-clear"
          onClick={handleClear}
          disabled={draft.length === 0}
        >
          Clear
        </button>
        <button
          type="button"
          className="fsm-close"
          data-testid="field-sort-close"
          onClick={onClose}
          aria-label="Close sort"
        >
          ✕
        </button>
      </div>

      <div className="fsm-body">
        {draft.length === 0 && (
          <p className="fsm-empty" data-testid="field-sort-empty">
            No sort. Rows are in Load order.
          </p>
        )}

        {draft.map((level, index) => (
          /* Keyed on the INDEX, deliberately. A level has no identity of its
             own — two levels may name the same field, and a field may move
             between positions — so index is the only stable handle. The rows
             hold no uncontrolled state (both controls are driven from
             `draft`), which is what makes an index key safe here. */
          <div className="fsm-level" key={index} data-testid={`field-sort-level-${index}`}>
            <span className="fsm-level-ord" aria-hidden="true">{index + 1}</span>

            <select
              className="fsm-select"
              data-testid={`field-sort-field-${index}`}
              aria-label={`Sort level ${index + 1} field`}
              value={level.fieldKey}
              onChange={(e) => setLevel(index, { fieldKey: e.target.value })}
            >
              <FieldPickerOptions
                fields={fields}
                hidden={hidden}
                value={level.fieldKey}
              />
            </select>

            <button
              type="button"
              className="fsm-dir"
              data-testid={`field-sort-dir-${index}`}
              onClick={() => toggleDirection(index)}
              aria-label={`Sort level ${index + 1} direction`}
            >
              {level.direction === 'asc' ? 'A → Z' : 'Z → A'}
            </button>

            <button
              type="button"
              className="fsm-remove"
              data-testid={`field-sort-remove-${index}`}
              onClick={() => removeLevel(index)}
              aria-label={`Remove sort level ${index + 1}`}
            >
              ✕
            </button>
          </div>
        ))}

        {/* NO HANDLER, NO CONTROL, applied to a data precondition rather than
            a prop: with no offerable field there is nothing an added level
            could name, so the control does not render at all. */}
        {firstKey && (
          <button
            type="button"
            className="fsm-add"
            data-testid="field-sort-add"
            onClick={addLevel}
          >
            + Add sort level
          </button>
        )}
      </div>

      <div className="fsm-footer">
        <button
          type="button"
          className="fsm-cancel"
          data-testid="field-sort-cancel"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="fsm-apply"
          data-testid="field-sort-apply"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
}
